/**
 * Time-of-day — a bare `HH:MM` string with no date and no timezone, e.g. an
 * itinerary event's `startTime` ("starts at 14:30"). Floating like a wall-clock
 * time: never shifted for the viewer, so it renders through UTC and the shown
 * digits are exactly the stored digits.
 *
 * Its own module rather than a guest in `wallClock.ts`, because it is its own
 * wire shape: `wallClock` is `YYYY-MM-DD HH:MM:SS`, this is `HH:MM`, and the
 * tiers exist precisely so those cannot drift back together.
 *
 * Display follows the device locale's clock (issue #165) — unlike the flight
 * tiers, which pin 24-hour aviation rendering (issue #164).
 */

import { deviceLocale } from './locale'

/**
 * Time-of-day `HH:MM` → display in the device locale clock: `2:30 PM` or
 * `14:30`. Returns the input unchanged if it isn't a time-of-day string, and
 * `null` for empty input.
 */
export const formatTimeOfDay = (str: string | null | undefined): string | null => {
	if (!str) return null
	const match = str.match(/^(\d{2}):(\d{2})$/)
	if (!match) return str
	const [, h, mi] = match
	const date = new Date(Date.UTC(2000, 0, 1, Number(h), Number(mi)))
	// timeStyle (not hour/minute components) so each locale keeps its preferred
	// pattern — de-DE pads midnight to "00:30", en-US reads "12:30 AM".
	return date.toLocaleTimeString(deviceLocale(), { timeStyle: 'short', timeZone: 'UTC' })
}
